import { gql } from "@apollo/client";
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import _ from 'lodash'

import Client from "data/_client"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { local } from 'data/lib/local-json'

import { PromiseWatch } from "./lib/promise-helpers"
import notificationService from "./notification";
import validationService from "data/validate"

// gql mutations

export const RefreshOrgSubscription = gql`
  mutation RefreshOrgSubscription($id: String!) {
    refreshOrgSubscription(id: $id) {
      id
    }
  }
`

// gql queries

export const GetOrganization = gql`
  query GetOrganization($id: String!) {
    organization(id: $id) {
      id
      multiuser
      name
      subscriptionid
      subscriptionstatus
      num_licenses
      userorganizations {
        id
        enabled
        expires
        licensetype {
          id
          name
        }
        roletype {
          id
          name
        }
        trial
        user {
          id
          email
          name
          password
        }
      }
    }
  }
`;

// private module logic //

type User = {
  approles: string[]
  defaultportfolios: number[]
  defaultrole: string
  email: string
  enabled: boolean
  expires: string
  license: string
  name: string
  organizationid: string
  orgrole: string
  trial: boolean
  userid: string
}
interface Users {
  error: string | null;
  users: User[]
}

const orgId = () => _.get(local("auth.profile"), 'organizationId');

const initialState: Users = {
  error: null,
  users: []
}
let usersMap: {[key: string]: User} = {}

const usersStream = new BehaviorSubject<Users>(initialState)

const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function get(userid: number): object {
  return usersMap[userid]
}

function clear(): void {
  usersStream.next({
    error: null,
    users: []
  })
  usersMap = {}
}

function getUsers(): Promise<User[] | { message: string }> {
  // TODO i don't think the _fetches_ helper is needed here since the orgId won't change often
  if (_fetches_.has(orgId())) return _fetches_.first(orgId())

  clear()
  usersStream.next({ error: null, users: usersStream.getValue()['users'] })

  return _fetches_.watch(orgId(), Client.get('user')
  .then((users: User[]) => {
    _.forEach(users, (user) => (usersMap[user.userid] = user))
    usersStream.next({ error: null, users })
    return users
  })
  .catch((error: { message: string }) => {
    usersStream.next({ users: usersStream.getValue()['users'], error: error.message })
    return error
  }))
}

const createUser = async(name:string, email:string): Promise<object | { message: string }> => {
  const user = {
    email,
    enabled: true,
    expires: '1/1/2030',
    license: 'professional',
    name,
    organizationid: orgId(),
    orgrole: 'user',
    trial: false,
  }

  return Client.post('user', user)
  .then((user: User) => {
    const users = usersStream.getValue()['users'];
    usersMap[user.userid] = user
    usersStream.next({ users: _.unionBy(users, [user], 'userid'), error: null });
    return user
  }).catch((error) => {
    usersStream.next({ users: usersStream.getValue()['users'], error: _.get(error, 'response.errors.0.message') })
    usersStream.getValue()['users']
    return error
  });
}

function updateUser(user: {userid:string}): Promise<User | { message: string }> {
  const currentValue = usersStream.getValue()['users']

  if (_.includes(user['approles'], 'Administrator') && user['orgrole'] == 'user')
    user['orgrole'] = 'admin'
  if (!_.includes(user['approles'], 'Administrator') && user['orgrole'] == 'admin') {
    user['orgrole'] = 'user'
    if (user.userid == local('auth.profile').user_id) {
      // TODO regenerate token/profile, otherwise refreshing page with existing token will re-sync them as admin due to apollo in-memory-cache
      notificationService.notify({ type: 'warn', message: 'You must log out for change to take effect', dismiss: 5000 })
    }
  }
  // if (!_.includes(user['approles'], 'Administrator') && user['orgrole'] == 'support')
  //   user['approles'] = _.concat(user['approles'], 'Administrator')

  const userid = user.userid
  const currentUser = _.find(currentValue, { userid })
  const changeset = diff_obj(user, currentUser)

  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => updateUser(currentUser),
    redofunc: () => updateUser(user)
  })

  // optimistic update
  let nextUsers = _.cloneDeep(currentValue)
  nextUsers.splice(_.findIndex(nextUsers, u => u.userid === userid), 1, user)
  usersStream.next({
    error: null,
    users: nextUsers
  })

  return _patches_.watch(userid, Client.patch(`user/${userid}`, changeset)
    .then((user: User) => {
      usersMap[user.userid] = user
      return user
    })
    .catch(async (error: { message: string }) => {
      if (error.message == 'Administrator approle must correspond to admin or support orgrole') {
        notificationService.notify({ type: 'danger', message: 'Administrator Applicaton Access must correspond to Admin or Support org role', dismiss: 5000 })
      }
      // on error overwrite optimistic update
      usersStream.next({ error: _.get(error, 'response.errors.0.message'), users: currentValue })
      return error
    })
    .finally(() => {
      validationService.fetch('user', userid)
    })
  )
}

// async function deleteUser(userid:number): Promise<boolean | { message: string }> {
//   const currentValue: object[] = usersStream.getValue()['users']
//   // filter out the deleted userid
//   const value = _(currentValue).filter((u) => u.userid !== userid).value()
//   value.forEach((u) => (usersMap[u.userid] = u))

//   console.log(value);
//   usersStream.next({ error: null, users: value })

//   // update the db
//   return _patches_.all(userid).then(() =>
//   Client.del(`user/${userid}`)
//     .then((user: object) => {
//       console.log(user);
//       delete usersMap[user['userid']]
//       push_undo({
//         undofunc: () => {
//           Client.post('user', user)
//           usersMap[user['userId']] = user
//           usersStream.next({ error: null, users: currentValue })
//         },
//         redofunc: () => deleteUser(userid)
//       })
//       // update already happened optimistically
//       return true
//     })
//     .catch((error: { message: string }) => {
//       // on error overwrite optimistic update
//       usersStream.next({ error, users: currentValue })
//       notificationService.notify({
//         type: 'danger',
//         message: error.message,
//         dismiss: 5000
//       })
//       getUsers()
//       return error
//     })
//   )
// }

// public module interface //

const usersService = {
  stream: () =>usersStream.asObservable(),
  createUser: (name:string, email:string) => createUser(name, email),
  // deleteUser: (userid:number) => deleteUser(userid),
  get: (userid:number) => get(userid),
  getCurrent: () => get(_.get(local("auth.profile"), 'user_id')),
  GetOrganization,
  getUsers,
  RefreshOrgSubscription,
  updateUser
}

export default usersService